import React from 'react';
import styled from 'styled-components';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  ExpandableMenu,
  MenuItem,
  MenuList,
  TransparentMenuButton,
} from '@grnhse/seedling/lib/birch/components/expandable_menu';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import ArrowDownIcon from '@grnhse/seedling/lib/birch/icons/arrow_down';
import InlineLabel from 'shared/components/birch/inline_label';
import { withLocalePrefix } from 'shared/utils/translation';
import type { ExcludeMatch } from 'talent_matching/types';

const t = withLocalePrefix('talent_matching.filter_row.does_not_have_match');
const connectorT = withLocalePrefix('talent_matching.filter_row.keywords');

const MATCH_LABELS: Record<ExcludeMatch, string> = {
  none_of: t('any'),
  not_all_of: t('all'),
};

const MATCHES = Object.keys(MATCH_LABELS) as ExcludeMatch[];

export const doesNotHaveConnector = (match: ExcludeMatch) =>
  match === 'not_all_of' ? connectorT('and') : connectorT('or');

const MatchButton = styled(TransparentMenuButton)`
  display: inline-flex;
  align-items: center;
  gap: ${Tokens.spacing['0.25']};
  padding: 0;
  font: inherit;
  font-weight: 500;
  color: inherit;
  text-decoration: underline;
`;

type DoesNotHaveLabelProps = {
  match: ExcludeMatch;
  onMatchChange: (match: ExcludeMatch) => void;
};

const DoesNotHaveLabel = ({ match, onMatchChange }: DoesNotHaveLabelProps) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Stack direction="row" spacing={0.5} alignItems="center">
      <InlineLabel>{t('label')}</InlineLabel>
      <ExpandableMenu>
        <MatchButton
          aria-label={t('menu_label', { match: MATCH_LABELS[match] })}
          data-provides="does-not-have-match"
        >
          {MATCH_LABELS[match]}
          <ArrowDownIcon size="sm" color="currentColor" />
        </MatchButton>
        <MenuList>
          {MATCHES.map((value) => (
            <MenuItem key={value} onSelect={() => onMatchChange(value)}>
              {MATCH_LABELS[value]}
            </MenuItem>
          ))}
        </MenuList>
      </ExpandableMenu>
    </Stack>
    <HelpTooltip
      label={
        <Stack spacing={1}>
          <span>{t('tooltip_any')}</span>
          <span>{t('tooltip_all')}</span>
        </Stack>
      }
      ariaLabel={`${t('tooltip_any')} ${t('tooltip_all')}`}
    />
  </Stack>
);

export default DoesNotHaveLabel;
